import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { adminApi, parseError } from "./api";
import { SCHEMAS, clientErrors, fromDoc, toPayload } from "./schemas";
import { ConfirmButton, FormField, StatusBadge } from "./ui";
import { fmtDate, fmtTime } from "@/lib/format";

const draftKey = (kind, id) => `brain-admin-draft:${kind}:${id}`;

export default function Editor({ kind }) {
  const cfg = SCHEMAS[kind];
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [values, setValues] = useState(isNew ? fromDoc(kind) : null);
  const [snapshot, setSnapshot] = useState(isNew ? JSON.stringify(fromDoc(kind)) : "");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [restored, setRestored] = useState(false);
  const [preview, setPreview] = useState(false);
  const [cats, setCats] = useState({});
  const dirty = values !== null && JSON.stringify(values) !== snapshot;

  useEffect(() => {
    if (kind === "team") return;
    adminApi.get("/categories")
      .then(({ data }) => setCats(Object.fromEntries(Object.entries(data).map(([k, list]) => [k, list.map((c) => c.name)]))))
      .catch((e) => toast.error(parseError(e).message));
  }, [kind]);

  const withOptions = (fd) => {
    if (!fd.source) return fd;
    const current = [values[fd.name]].flat().filter(Boolean);
    return { ...fd, options: [...new Set([...(cats[fd.source] || []), ...current])] };
  };

  const apply = useCallback((d) => {
    const v = fromDoc(kind, d);
    setDoc(d);
    setValues(v);
    setSnapshot(JSON.stringify(v));
    setErrors({});
    setMessage("");
  }, [kind]);

  useEffect(() => {
    const saved = sessionStorage.getItem(draftKey(kind, id));
    if (isNew) {
      if (saved) { setValues(JSON.parse(saved)); setRestored(true); }
      return;
    }
    adminApi.get(`/${kind}/${id}`)
      .then(({ data }) => {
        apply(data);
        if (saved) { setValues(JSON.parse(saved)); setRestored(true); }
      })
      .catch((e) => setLoadError(parseError(e).message));
  }, [kind, id, isNew, apply]);

  useEffect(() => {
    if (dirty) sessionStorage.setItem(draftKey(kind, id), JSON.stringify(values));
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, values, kind, id]);

  const fail = (e) => {
    const p = parseError(e);
    setErrors(p.fields);
    setMessage(p.message);
    toast.error(p.message);
  };

  const save = async () => {
    if (busy) return null;
    const local = clientErrors(kind, values);
    if (Object.keys(local).length) {
      setErrors(local);
      setMessage("Please complete or correct the highlighted fields. Nothing was saved.");
      toast.error("Some required fields are missing or invalid");
      return null;
    }
    setBusy(true);
    try {
      const payload = toPayload(kind, values);
      const { data } = isNew ? await adminApi.post(`/${kind}`, payload) : await adminApi.put(`/${kind}/${id}`, payload);
      sessionStorage.removeItem(draftKey(kind, id));
      apply(data);
      setRestored(false);
      toast.success(isNew ? `Draft ${cfg.singular} created` : "Changes saved");
      if (isNew) navigate(`/admin/${kind}/${data.id}`, { replace: true });
      return data;
    } catch (e) {
      fail(e);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status, label) => {
    const current = dirty || isNew ? await save() : doc;
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/${kind}/${current.id}/status`, { status });
      apply(data);
      toast.success(`${label}: ${cfg.singular} is now ${status}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const back = (e) => {
    if (dirty && !window.confirm("You have unsaved changes. Leave this page? (A copy is kept on this device.)")) e.preventDefault();
  };

  if (loadError) return <p className="text-[#B42318]" role="alert" data-testid="admin-load-error">{loadError}</p>;
  if (!values) return <p role="status" className="text-ink-muted">Loading…</p>;
  const status = doc?.status || "Draft";

  return (
    <div data-testid={`admin-editor-${kind}`}>
      <Link to={`/admin/${kind}`} onClick={back} className="inline-flex items-center gap-2 text-sm font-bold text-brand" data-testid="admin-editor-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All {cfg.label.toLowerCase()}
      </Link>
      <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-brand">{isNew ? `New ${cfg.singular}` : values[cfg.titleField] || `Edit ${cfg.singular}`}</h1>

      {restored && (
        <div className="mt-4 flex flex-wrap items-center gap-3 border border-brand-green bg-pale p-3 text-sm" role="status" data-testid="admin-restored-draft">
          Unsaved changes from your last session were restored.
          <button type="button" className="font-bold text-brand underline" onClick={() => { sessionStorage.removeItem(draftKey(kind, id)); setValues(doc ? fromDoc(kind, doc) : fromDoc(kind)); setRestored(false); }} data-testid="admin-discard-draft">Discard them</button>
        </div>
      )}
      {message && <div className="mt-4 border border-[#B42318] bg-[#FDECEA] p-3 text-sm font-semibold text-[#B42318]" role="alert" data-testid="admin-form-error">{message}</div>}

      <form className="mt-8 grid gap-8 lg:grid-cols-12" onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
        <div className="space-y-8 lg:col-span-8">
          {cfg.sections.map((s) => (
            <fieldset key={s.title} className="space-y-5 border border-line bg-white p-6">
              <legend className="px-1 text-sm font-extrabold uppercase tracking-[0.12em] text-brand">{s.title}</legend>
              {s.fields.map((fd) => (
                <FormField key={fd.name} field={withOptions(fd)} value={values[fd.name]} error={errors[fd.name]} onChange={(n, v) => setValues((cur) => ({ ...cur, [n]: v }))} />
              ))}
            </fieldset>
          ))}
        </div>
        <aside className="lg:col-span-4">
          <div className="sticky top-6 space-y-4 border border-line bg-white p-6">
            <div className="flex items-center justify-between"><span className="text-sm font-bold">Status</span><StatusBadge status={status} /></div>
            {doc?.updatedAt && <p className="text-xs text-ink-muted" data-testid="admin-updated-at">Last updated {fmtDate(doc.updatedAt)}, {fmtTime(doc.updatedAt)} IST</p>}
            {dirty && <p className="text-xs font-bold text-[#8A5A00]" data-testid="admin-unsaved">Unsaved changes</p>}
            <button type="submit" disabled={busy || (!dirty && !isNew)} className="admin-btn w-full" data-testid="admin-save-button">
              {busy ? "Saving…" : isNew ? "Save as draft" : "Save changes"}
            </button>
            <button type="button" onClick={() => setPreview(true)} className="admin-btn-outline inline-flex w-full items-center justify-center gap-2" data-testid="admin-preview-button">
              <Eye className="h-4 w-4" aria-hidden="true" /> Preview
            </button>
            <div className="space-y-2 border-t border-line pt-4">
              {(cfg.actions[status] || []).map(([label, next, confirm]) =>
                confirm ? (
                  <ConfirmButton key={label} label={label} disabled={busy} className="admin-btn-outline w-full" testId={`admin-action-${next.toLowerCase()}`}
                    title={`${label}?`} description={`This ${cfg.singular} will change from ${status} to ${next}.${dirty ? " Your unsaved edits will be saved first." : ""}`}
                    onConfirm={() => changeStatus(next, label)} />
                ) : (
                  <button key={label} type="button" disabled={busy} onClick={() => changeStatus(next, label)} className="admin-btn-green w-full" data-testid={`admin-action-${next.toLowerCase()}`}>
                    {dirty || isNew ? `Save & ${label.toLowerCase()}` : label}
                  </button>
                )
              )}
            </div>
            {doc?.slug && status === "Published" && kind !== "team" && (
              <a href={`/${kind === "stories" ? "research" : "events"}/${doc.slug}`} target="_blank" rel="noopener noreferrer" className="block text-sm font-bold text-brand underline" data-testid="admin-view-public">View on public site</a>
            )}
          </div>
        </aside>
      </form>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogTitle className="sr-only">Preview</DialogTitle>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">Preview — unsaved edits included, not visible to the public</p>
          {cfg.preview(values)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
